import ReportType from '@/Enums/Looker/ReportType.enum';
import MapScale from '@/Enums/Map/MapScale.enum';
import { BeeswaxReportProperties } from '@/Enums/Report/BeeswaxReportProperties.enum';
import ReportRequest from '@/Enums/ReportingDashboard/OmnichannelReportRequest.enum';
import ReportView from '@/Enums/ReportingDashboard/ReportView/ReportView.enum';
import ViewType from '@/Enums/ReportingDashboard/ViewType.enum';
import SnowflakeDeliveryReportType from '@/Enums/Snowflake/DeliveryReportType.enum';
import type { IGeoJson } from '@/Interfaces/Geo/GeoJson.interface';
import type {
  IBaseDataReport,
  IPlacementReport,
  ITotalReport,
} from '@/Interfaces/ReportingDashboard/Beeswax/Report.interface';
import type {
  IAdvertiserReportRecord,
  IBaseReportRecord,
  ICampaignNameReportRecord,
  IChannelReportRecord,
  ICityReportRecord,
  ICongressionalDistrictsReportRecord,
  IContentChannelReportRecord,
  IContentNetworkReportRecord,
  ICountyReportRecord,
  ICreativeReportRecord,
  IDateReportRecord,
  IDayOfWeekReportRecord,
  IDaypartReportRecord,
  IDealReportRecord,
  IDeviceReportRecord,
  IDmaReportRecord,
  IMonthReportRecord,
  IPublisherReportRecord,
  IQuarterReportRecord,
  IStateReportRecord,
  IWeekReportRecord,
  IZipReportRecord,
} from '@/Interfaces/ReportingDashboard/Beeswax/ReportRecord.interface';
import type { IAbortSignalThunkArg } from '@/Interfaces/Slices/AsyncThunk.interface';
import type { IState } from '@/Interfaces/Slices/ReportingDashboard/Beeswax/State.interface';
import {
  GEO_BOUNDARIES,
  GEO_JOINT_BOUNDARIES,
  REPORTING_BEESWAX_CONTENT_DIMENSION_ENDPOINT,
  ZIPCODE_ENDPOINT,
} from '@/constants/endpoints';
import { MAP_SCALES, borderIOHeaders } from '@/constants/geo';
import { defaultTotals } from '@/constants/reportingDashboard/beeswax';
import { DEFAULT_LOADING_REQUEST_ID } from '@/constants/slice';
import { EMPTY_FEATURE_COLLECTION, isPostableBoundariesBody } from '@/helpers/boundaries';
import { getBoundariesRequestBody, getCongressionalBoundariesRequestBody } from '@/helpers/geo';
import { lookerReportRequestHandler } from '@/helpers/reportingDashboard/beeswax';
import { RootState } from '@/store';
import type { OmnichannelReportView } from '@/types/ReportingDashboard/Omnichannel.type';
import type { RequestMap } from '@/types/RequestMap.types';
import { AsyncThunk, AsyncThunkOptions, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

enum ActionPrefix {
  Totals = 'reportingDashboardBeeswax/getTotalsReportData',

  DateDeliveryReport = 'reportingDashboardBeeswax/getDateDeliveryReport',
  WeekDeliveryReport = 'reportingDashboardBeeswax/getWeekDeliveryReport',
  MonthDeliveryReport = 'reportingDashboardBeeswax/getMonthDeliveryReport',
  QuarterDeliveryReport = 'reportingDashboardBeeswax/getQuarterDeliveryReport',
  ChannelDeliveryReport = 'reportingDashboardBeeswax/getChannelDeliveryReport',
  DeviceDeliveryReport = 'reportingDashboardBeeswax/getDeviceDeliveryReport',
  AdvertiserDeliveryReport = 'reportingDashboardBeeswax/getAdvertiserDeliveryReport',
  CampaignDeliveryReport = 'reportingDashboardBeeswax/getCampaignDeliveryReport',
  PlacementDeliveryReport = 'reportingDashboardBeeswax/getPlacementDeliveryReport',
  CreativeDeliveryReport = 'reportingDashboardBeeswax/getCreativeDeliveryReport',
  PublisherDeliveryReport = 'reportingDashboardBeeswax/getPublisherDeliveryReport',
  DayOfWeekDeliveryReport = 'reportingDashboardBeeswax/getDayOfWeekDeliveryReport',
  DaypartDeliveryReport = 'reportingDashboardBeeswax/getDaypartDeliveryReport',
  DealsDeliveryReport = 'reportingDashboardBeeswax/getDealsDeliveryReport',
  ContentNetworkDeliveryReport = 'reportingDashboardBeeswax/getContentNetworkDeliveryReport',
  ContentChannelDeliveryReport = 'reportingDashboardBeeswax/getContentChannelDeliveryReport',
  StateDeliveryReport = 'reportingDashboardBeeswax/getStateDeliveryReport',
  DmaDeliveryReport = 'reportingDashboardBeeswax/getDmaDeliveryReport',
  CountyDeliveryReport = 'reportingDashboardBeeswax/getCountyDeliveryReport',
  ZipDeliveryReport = 'reportingDashboardBeeswax/getZipDeliveryReport',
  CongressionalDeliveryReport = 'reportingDashboardBeeswax/getCongressionalDeliveryReport',
  CityDeliveryReport = 'reportingDashboardBeeswax/getCityDeliveryReport',

  DateAttributionReport = 'reportingDashboardBeeswax/getDateAttributionReport',
  WeekAttributionReport = 'reportingDashboardBeeswax/getWeekAttributionReport',
  MonthAttributionReport = 'reportingDashboardBeeswax/getMonthAttributionReport',
  QuarterAttributionReport = 'reportingDashboardBeeswax/getQuarterAttributionReport',
  ChannelAttributionReport = 'reportingDashboardBeeswax/getChannelAttributionReport',
  DeviceAttributionReport = 'reportingDashboardBeeswax/getDeviceAttributionReport',
  AdvertiserAttributionReport = 'reportingDashboardBeeswax/getAdvertiserAttributionReport',
  CampaignAttributionReport = 'reportingDashboardBeeswax/getCampaignAttributionReport',
  PlacementAttributionReport = 'reportingDashboardBeeswax/getPlacementAttributionReport',
  CreativeAttributionReport = 'reportingDashboardBeeswax/getCreativeAttributionReport',
  PublisherAttributionReport = 'reportingDashboardBeeswax/getPublisherAttributionReport',
  DayOfWeekAttributionReport = 'reportingDashboardBeeswax/getDayOfWeekAttributionReport',
  DaypartAttributionReport = 'reportingDashboardBeeswax/getDaypartAttributionReport',
  DealsAttributionReport = 'reportingDashboardBeeswax/getDealsAttributionReport',
  StateAttributionReport = 'reportingDashboardBeeswax/getStateAttributionReport',
  DmaAttributionReport = 'reportingDashboardBeeswax/getDmaAttributionReport',
  CountyAttributionReport = 'reportingDashboardBeeswax/getCountyAttributionReport',
  ZipAttributionReport = 'reportingDashboardBeeswax/getZipAttributionReport',
  CongressionalAttributionReport = 'reportingDashboardBeeswax/getCongressionalAttributionReport',
  CityAttributionReport = 'reportingDashboardBeeswax/getCityAttributionReport',

  ZipBoundaries = 'reportingDashboardBeeswax/getZipBoundaries',
  DmaBoundaries = 'reportingDashboardBeeswax/getDmaBoundaries',
  StateBoundaries = 'reportingDashboardBeeswax/getStateBoundaries',
  CountyBoundaries = 'reportingDashboardBeeswax/getCountyBoundaries',
  CongressionalDistrictBoundaries = 'reportingDashboardBeeswax/getCongressionalDistrictBoundaries',
  CityBoundaries = 'reportingDashboardBeeswax/getCityBoundaries',
}

const shouldFetchExplicitReport = (report: IBaseReportRecord[], requestId: string | null) => {
  if (requestId === DEFAULT_LOADING_REQUEST_ID) {
    return true;
  }
  return !report?.length && !requestId;
};

const getExplicitReportThunkOptions = (
  dataField: keyof IState,
  requestIdField?: keyof IState,
): AsyncThunkOptions<IAbortSignalThunkArg> => ({
  condition: (_, { getState }) => {
    const requestIdKey = requestIdField ?? (`${dataField}RequestId` as keyof IState);
    const {
      reportingDashboardBeeswax: { [dataField]: data, [requestIdKey]: requestId },
    } = getState() as RootState;

    return shouldFetchExplicitReport(data as IBaseReportRecord[], requestId as string);
  },
});

// Gate strictly on the initial/after-reset sentinel so an empty report (valid for a
// seat/date with no log-level rows) is cached instead of refetched in a loop.
const getContentDimensionThunkOptions = (dataField: keyof IState): AsyncThunkOptions<IAbortSignalThunkArg> => ({
  condition: (_, { getState }) => {
    const requestIdKey = `${dataField}RequestId` as keyof IState;
    const {
      reportingDashboardBeeswax: { [requestIdKey]: requestId },
    } = getState() as RootState;

    return requestId === DEFAULT_LOADING_REQUEST_ID;
  },
});

//#region graphs
export const getTotalReportData = createAsyncThunk<ITotalReport, IAbortSignalThunkArg>(
  ActionPrefix.Totals,
  lookerReportRequestHandler(ReportType.Total, { totals: defaultTotals }),
);

//#endregion

//#region delivery
export const getDateDeliveryReport = createAsyncThunk<IBaseDataReport<IDateReportRecord>, IAbortSignalThunkArg>(
  ActionPrefix.DateDeliveryReport,
  lookerReportRequestHandler(ReportType.DateDelivery, { report: [] as IDateReportRecord[] }),
  getExplicitReportThunkOptions('dateDeliveryReport'),
);

export const getWeekDeliveryReport = createAsyncThunk<IBaseDataReport<IWeekReportRecord>, IAbortSignalThunkArg>(
  ActionPrefix.WeekDeliveryReport,
  lookerReportRequestHandler(ReportType.WeekDelivery, { report: [] as IWeekReportRecord[] }),
  getExplicitReportThunkOptions('weekDeliveryReport'),
);

export const getMonthDeliveryReport = createAsyncThunk<IBaseDataReport<IMonthReportRecord>, IAbortSignalThunkArg>(
  ActionPrefix.MonthDeliveryReport,
  lookerReportRequestHandler(ReportType.MonthDelivery, { report: [] as IMonthReportRecord[] }),
  getExplicitReportThunkOptions('monthDeliveryReport'),
);

export const getQuarterDeliveryReport = createAsyncThunk<IBaseDataReport<IQuarterReportRecord>, IAbortSignalThunkArg>(
  ActionPrefix.QuarterDeliveryReport,
  lookerReportRequestHandler(ReportType.QuarterDelivery, { report: [] as IQuarterReportRecord[] }),
  getExplicitReportThunkOptions('quarterDeliveryReport'),
);

export const getChannelDeliveryReport = createAsyncThunk<IBaseDataReport<IChannelReportRecord>, IAbortSignalThunkArg>(
  ActionPrefix.ChannelDeliveryReport,
  lookerReportRequestHandler(ReportType.ChannelDelivery),
  getExplicitReportThunkOptions('channelDeliveryReport'),
);

export const getDeviceDeliveryReport = createAsyncThunk<IBaseDataReport<IDeviceReportRecord>, IAbortSignalThunkArg>(
  ActionPrefix.DeviceDeliveryReport,
  lookerReportRequestHandler(ReportType.DeviceDelivery),
  getExplicitReportThunkOptions('deviceDeliveryReport'),
);

export const getAdvertiserDeliveryReport = createAsyncThunk<
  IBaseDataReport<IAdvertiserReportRecord>,
  IAbortSignalThunkArg
>(
  ActionPrefix.AdvertiserDeliveryReport,
  lookerReportRequestHandler(ReportType.AdvertiserDelivery),
  getExplicitReportThunkOptions('advertiserDeliveryReport'),
);

export const getCampaignDeliveryReport = createAsyncThunk<
  IBaseDataReport<ICampaignNameReportRecord>,
  IAbortSignalThunkArg
>(
  ActionPrefix.CampaignDeliveryReport,
  lookerReportRequestHandler(ReportType.CampaignDelivery),
  getExplicitReportThunkOptions('campaignDeliveryReport'),
);

export const getPlacementDeliveryReport = createAsyncThunk<IPlacementReport, IAbortSignalThunkArg>(
  ActionPrefix.PlacementDeliveryReport,
  lookerReportRequestHandler(ReportType.PlacementDelivery),
  getExplicitReportThunkOptions('placementDeliveryReport'),
);

export const getCreativeDeliveryReport = createAsyncThunk<IBaseDataReport<ICreativeReportRecord>, IAbortSignalThunkArg>(
  ActionPrefix.CreativeDeliveryReport,
  lookerReportRequestHandler(ReportType.CreativeDelivery),
  getExplicitReportThunkOptions('creativeDeliveryReport'),
);

export const getPublisherDeliveryReport = createAsyncThunk<
  IBaseDataReport<IPublisherReportRecord>,
  IAbortSignalThunkArg
>(
  ActionPrefix.PublisherDeliveryReport,
  lookerReportRequestHandler(ReportType.PublisherDelivery),
  getExplicitReportThunkOptions('publisherDeliveryReport'),
);

export const getDayOfWeekDeliveryReport = createAsyncThunk<
  IBaseDataReport<IDayOfWeekReportRecord>,
  IAbortSignalThunkArg
>(
  ActionPrefix.DayOfWeekDeliveryReport,
  lookerReportRequestHandler(ReportType.DayOfWeekDelivery),
  getExplicitReportThunkOptions('dayOfWeekDeliveryReport'),
);

export const getDaypartDeliveryReport = createAsyncThunk<IBaseDataReport<IDaypartReportRecord>, IAbortSignalThunkArg>(
  ActionPrefix.DaypartDeliveryReport,
  lookerReportRequestHandler(ReportType.DaypartDelivery),
  getExplicitReportThunkOptions('daypartDeliveryReport'),
);

export const getDealsDeliveryReport = createAsyncThunk<IBaseDataReport<IDealReportRecord>, IAbortSignalThunkArg>(
  ActionPrefix.DealsDeliveryReport,
  lookerReportRequestHandler(ReportType.DealsDelivery),
  getExplicitReportThunkOptions('dealsDeliveryReport'),
);

export const getContentNetworkDeliveryReport = createAsyncThunk<
  IBaseDataReport<IContentNetworkReportRecord>,
  IAbortSignalThunkArg
>(
  ActionPrefix.ContentNetworkDeliveryReport,
  lookerReportRequestHandler<IBaseDataReport<IContentNetworkReportRecord>>(
    SnowflakeDeliveryReportType.ContentNetworkDelivery,
    { report: [] },
    { url: REPORTING_BEESWAX_CONTENT_DIMENSION_ENDPOINT },
  ),
  getContentDimensionThunkOptions('contentNetworkDeliveryReport'),
);

export const getContentChannelDeliveryReport = createAsyncThunk<
  IBaseDataReport<IContentChannelReportRecord>,
  IAbortSignalThunkArg
>(
  ActionPrefix.ContentChannelDeliveryReport,
  lookerReportRequestHandler<IBaseDataReport<IContentChannelReportRecord>>(
    SnowflakeDeliveryReportType.ContentChannelDelivery,
    { report: [] },
    { url: REPORTING_BEESWAX_CONTENT_DIMENSION_ENDPOINT },
  ),
  getContentDimensionThunkOptions('contentChannelDeliveryReport'),
);

export const getStateDeliveryReport = createAsyncThunk<IBaseDataReport<IStateReportRecord>, IAbortSignalThunkArg>(
  ActionPrefix.StateDeliveryReport,
  lookerReportRequestHandler(ReportType.StateDelivery),
  getExplicitReportThunkOptions('stateDeliveryReport'),
);

export const getDmaDeliveryReport = createAsyncThunk<IBaseDataReport<IDmaReportRecord>, IAbortSignalThunkArg>(
  ActionPrefix.DmaDeliveryReport,
  lookerReportRequestHandler(ReportType.DmaDelivery),
  getExplicitReportThunkOptions('dmaDeliveryReport'),
);

export const getCountyDeliveryReport = createAsyncThunk<IBaseDataReport<ICountyReportRecord>, IAbortSignalThunkArg>(
  ActionPrefix.CountyDeliveryReport,
  lookerReportRequestHandler(ReportType.CountyDelivery),
  getExplicitReportThunkOptions('countyDeliveryReport'),
);

export const getZipDeliveryReport = createAsyncThunk<IBaseDataReport<IZipReportRecord>, IAbortSignalThunkArg>(
  ActionPrefix.ZipDeliveryReport,
  lookerReportRequestHandler(ReportType.ZipDelivery),
  getExplicitReportThunkOptions('zipDeliveryReport'),
);

export const getCongressionalDeliveryReport = createAsyncThunk<
  IBaseDataReport<ICongressionalDistrictsReportRecord>,
  IAbortSignalThunkArg
>(
  ActionPrefix.CongressionalDeliveryReport,
  lookerReportRequestHandler(ReportType.CongressionalDistrictDelivery),
  getExplicitReportThunkOptions('congressionalDistrictDeliveryReport'),
);

export const getCityDeliveryReport = createAsyncThunk<IBaseDataReport<ICityReportRecord>, IAbortSignalThunkArg>(
  ActionPrefix.CityDeliveryReport,
  lookerReportRequestHandler(ReportType.CityDelivery),
  getExplicitReportThunkOptions('cityDeliveryReport'),
);
//#endregion

//#region attribution
export const getDateAttributionReport = createAsyncThunk<IBaseDataReport<IDateReportRecord>, IAbortSignalThunkArg>(
  ActionPrefix.DateAttributionReport,
  lookerReportRequestHandler(ReportType.DateAttribution, { report: [] as IDateReportRecord[] }),
  getExplicitReportThunkOptions('dateAttributionReport'),
);

export const getWeekAttributionReport = createAsyncThunk<IBaseDataReport<IWeekReportRecord>, IAbortSignalThunkArg>(
  ActionPrefix.WeekAttributionReport,
  lookerReportRequestHandler(ReportType.WeekAttribution, { report: [] as IWeekReportRecord[] }),
  getExplicitReportThunkOptions('weekAttributionReport'),
);

export const getMonthAttributionReport = createAsyncThunk<IBaseDataReport<IMonthReportRecord>, IAbortSignalThunkArg>(
  ActionPrefix.MonthAttributionReport,
  lookerReportRequestHandler(ReportType.MonthAttribution, { report: [] as IMonthReportRecord[] }),
  getExplicitReportThunkOptions('monthAttributionReport'),
);

export const getQuarterAttributionReport = createAsyncThunk<
  IBaseDataReport<IQuarterReportRecord>,
  IAbortSignalThunkArg
>(
  ActionPrefix.QuarterAttributionReport,
  lookerReportRequestHandler(ReportType.QuarterAttribution, { report: [] as IQuarterReportRecord[] }),
  getExplicitReportThunkOptions('quarterAttributionReport'),
);

export const getChannelAttributionReport = createAsyncThunk<
  IBaseDataReport<IChannelReportRecord>,
  IAbortSignalThunkArg
>(
  ActionPrefix.ChannelAttributionReport,
  lookerReportRequestHandler(ReportType.ChannelAttribution),
  getExplicitReportThunkOptions('channelAttributionReport'),
);

export const getDeviceAttributionReport = createAsyncThunk<IBaseDataReport<IDeviceReportRecord>, IAbortSignalThunkArg>(
  ActionPrefix.DeviceAttributionReport,
  lookerReportRequestHandler(ReportType.DeviceAttribution),
  getExplicitReportThunkOptions('deviceAttributionReport'),
);

export const getAdvertiserAttributionReport = createAsyncThunk<
  IBaseDataReport<IAdvertiserReportRecord>,
  IAbortSignalThunkArg
>(
  ActionPrefix.AdvertiserAttributionReport,
  lookerReportRequestHandler(ReportType.AdvertiserAttribution),
  getExplicitReportThunkOptions('advertiserAttributionReport'),
);

export const getCampaignAttributionReport = createAsyncThunk<
  IBaseDataReport<ICampaignNameReportRecord>,
  IAbortSignalThunkArg
>(
  ActionPrefix.CampaignAttributionReport,
  lookerReportRequestHandler(ReportType.CampaignAttribution),
  getExplicitReportThunkOptions('campaignAttributionReport'),
);

export const getPlacementAttributionReport = createAsyncThunk<IPlacementReport, IAbortSignalThunkArg>(
  ActionPrefix.PlacementAttributionReport,
  lookerReportRequestHandler(ReportType.PlacementAttribution),
  getExplicitReportThunkOptions('placementAttributionReport'),
);

export const getCreativeAttributionReport = createAsyncThunk<
  IBaseDataReport<ICreativeReportRecord>,
  IAbortSignalThunkArg
>(
  ActionPrefix.CreativeAttributionReport,
  lookerReportRequestHandler(ReportType.CreativeAttribution),
  getExplicitReportThunkOptions('creativeAttributionReport'),
);

export const getPublisherAttributionReport = createAsyncThunk<
  IBaseDataReport<IPublisherReportRecord>,
  IAbortSignalThunkArg
>(
  ActionPrefix.PublisherAttributionReport,
  lookerReportRequestHandler(ReportType.PublisherAttribution),
  getExplicitReportThunkOptions('publisherAttributionReport'),
);

export const getDayOfWeekAttributionReport = createAsyncThunk<
  IBaseDataReport<IDayOfWeekReportRecord>,
  IAbortSignalThunkArg
>(
  ActionPrefix.DayOfWeekAttributionReport,
  lookerReportRequestHandler(ReportType.DayOfWeekAttribution),
  getExplicitReportThunkOptions('dayOfWeekAttributionReport'),
);

export const getDaypartAttributionReport = createAsyncThunk<
  IBaseDataReport<IDaypartReportRecord>,
  IAbortSignalThunkArg
>(
  ActionPrefix.DaypartAttributionReport,
  lookerReportRequestHandler(ReportType.DaypartAttribution),
  getExplicitReportThunkOptions('daypartAttributionReport'),
);

export const getDealsAttributionReport = createAsyncThunk<IBaseDataReport<IDealReportRecord>, IAbortSignalThunkArg>(
  ActionPrefix.DealsAttributionReport,
  lookerReportRequestHandler(ReportType.DealsAttribution),
  getExplicitReportThunkOptions('dealsAttributionReport'),
);

export const getStateAttributionReport = createAsyncThunk<IBaseDataReport<IStateReportRecord>, IAbortSignalThunkArg>(
  ActionPrefix.StateAttributionReport,
  lookerReportRequestHandler(ReportType.StateAttribution),
  getExplicitReportThunkOptions('stateAttributionReport'),
);

export const getDmaAttributionReport = createAsyncThunk<IBaseDataReport<IDmaReportRecord>, IAbortSignalThunkArg>(
  ActionPrefix.DmaAttributionReport,
  lookerReportRequestHandler(ReportType.DmaAttribution),
  getExplicitReportThunkOptions('dmaAttributionReport'),
);

export const getCountyAttributionReport = createAsyncThunk<IBaseDataReport<ICountyReportRecord>, IAbortSignalThunkArg>(
  ActionPrefix.CountyAttributionReport,
  lookerReportRequestHandler(ReportType.CountyAttribution),
  getExplicitReportThunkOptions('countyAttributionReport'),
);

export const getZipAttributionReport = createAsyncThunk<IBaseDataReport<IZipReportRecord>, IAbortSignalThunkArg>(
  ActionPrefix.ZipAttributionReport,
  lookerReportRequestHandler(ReportType.ZipAttribution),
  getExplicitReportThunkOptions('zipAttributionReport'),
);

export const getCongressionalAttributionReport = createAsyncThunk<
  IBaseDataReport<ICongressionalDistrictsReportRecord>,
  IAbortSignalThunkArg
>(
  ActionPrefix.CongressionalAttributionReport,
  lookerReportRequestHandler(ReportType.CongressionalDistrictAttribution),
  getExplicitReportThunkOptions('congressionalDistrictAttributionReport'),
);

export const getCityAttributionReport = createAsyncThunk<IBaseDataReport<ICityReportRecord>, IAbortSignalThunkArg>(
  ActionPrefix.CityAttributionReport,
  lookerReportRequestHandler(ReportType.CityAttribution),
  getExplicitReportThunkOptions('cityAttributionReport'),
);
//#endregion

//#region dispatch map
type ActionSet = { action: AsyncThunk<any, any, any>; reportRequest: ReportRequest };

export const mapGraphDispatch: Record<MapScale, ActionSet> = {
  [MapScale.Dma]: {
    action: getDmaDeliveryReport,
    reportRequest: ReportRequest.DmaDelivery,
  },
  [MapScale.County]: { action: getCountyDeliveryReport, reportRequest: ReportRequest.CountyDelivery },
  [MapScale.Zip]: { action: getZipDeliveryReport, reportRequest: ReportRequest.ZipDelivery },
  [MapScale.CongressionalDistricts]: {
    action: getCongressionalDeliveryReport,
    reportRequest: ReportRequest.CongressionalDelivery,
  },
  [MapScale.City]: { action: getCityDeliveryReport, reportRequest: ReportRequest.CityDelivery },
  [MapScale.State]: { action: getStateDeliveryReport, reportRequest: ReportRequest.StateDelivery },
};

const deliveryReportDispatch: Record<OmnichannelReportView, ActionSet> = {
  [ReportView.Date]: { action: getDateDeliveryReport, reportRequest: ReportRequest.DateDelivery },
  [ReportView.Week]: { action: getWeekDeliveryReport, reportRequest: ReportRequest.WeekDelivery },
  [ReportView.Month]: { action: getMonthDeliveryReport, reportRequest: ReportRequest.MonthDelivery },
  [ReportView.Quarter]: { action: getQuarterDeliveryReport, reportRequest: ReportRequest.QuarterDelivery },
  [ReportView.Channel]: { action: getChannelDeliveryReport, reportRequest: ReportRequest.ChannelDelivery },
  [ReportView.Device]: { action: getDeviceDeliveryReport, reportRequest: ReportRequest.DeviceDelivery },
  [ReportView.Advertiser]: { action: getAdvertiserDeliveryReport, reportRequest: ReportRequest.AdvertiserDelivery },
  [ReportView.Campaign]: { action: getCampaignDeliveryReport, reportRequest: ReportRequest.CampaignDelivery },
  [ReportView.Placement]: { action: getPlacementDeliveryReport, reportRequest: ReportRequest.PlacementDelivery },
  [ReportView.Creative]: { action: getCreativeDeliveryReport, reportRequest: ReportRequest.CreativeDelivery },
  [ReportView.Publisher]: { action: getPublisherDeliveryReport, reportRequest: ReportRequest.PublisherDelivery },
  [ReportView.DaysOfWeek]: { action: getDayOfWeekDeliveryReport, reportRequest: ReportRequest.DayOfWeekDelivery },
  [ReportView.Daypart]: { action: getDaypartDeliveryReport, reportRequest: ReportRequest.DaypartDelivery },
  [ReportView.Deals]: { action: getDealsDeliveryReport, reportRequest: ReportRequest.DealsDelivery },
  [ReportView.ContentNetwork]: {
    action: getContentNetworkDeliveryReport,
    reportRequest: ReportRequest.ContentNetworkDelivery,
  },
  [ReportView.ContentChannel]: {
    action: getContentChannelDeliveryReport,
    reportRequest: ReportRequest.ContentChannelDelivery,
  },
  [MapScale.Dma]: { action: getDmaDeliveryReport, reportRequest: ReportRequest.DmaDelivery },
  [MapScale.County]: { action: getCountyDeliveryReport, reportRequest: ReportRequest.CountyDelivery },
  [MapScale.Zip]: { action: getZipDeliveryReport, reportRequest: ReportRequest.ZipDelivery },
  [MapScale.CongressionalDistricts]: {
    action: getCongressionalDeliveryReport,
    reportRequest: ReportRequest.CongressionalDelivery,
  },
  [MapScale.City]: { action: getCityDeliveryReport, reportRequest: ReportRequest.CityDelivery },
  [MapScale.State]: { action: getStateDeliveryReport, reportRequest: ReportRequest.StateDelivery },
};

const attributionReportDispatch: Record<OmnichannelReportView, ActionSet> = {
  [ReportView.Date]: { action: getDateAttributionReport, reportRequest: ReportRequest.DateAttribution },
  [ReportView.Week]: { action: getWeekAttributionReport, reportRequest: ReportRequest.WeekAttribution },
  [ReportView.Month]: { action: getMonthAttributionReport, reportRequest: ReportRequest.MonthAttribution },
  [ReportView.Quarter]: { action: getQuarterAttributionReport, reportRequest: ReportRequest.QuarterAttribution },
  [ReportView.Channel]: { action: getChannelAttributionReport, reportRequest: ReportRequest.ChannelAttribution },
  [ReportView.Device]: { action: getDeviceAttributionReport, reportRequest: ReportRequest.DeviceAttribution },
  [ReportView.Advertiser]: {
    action: getAdvertiserAttributionReport,
    reportRequest: ReportRequest.AdvertiserAttribution,
  },
  [ReportView.Campaign]: { action: getCampaignAttributionReport, reportRequest: ReportRequest.CampaignAttribution },
  [ReportView.Placement]: { action: getPlacementAttributionReport, reportRequest: ReportRequest.PlacementAttribution },
  [ReportView.Creative]: { action: getCreativeAttributionReport, reportRequest: ReportRequest.CreativeAttribution },
  [ReportView.Publisher]: { action: getPublisherAttributionReport, reportRequest: ReportRequest.PublisherAttribution },
  [ReportView.DaysOfWeek]: { action: getDayOfWeekAttributionReport, reportRequest: ReportRequest.DayOfWeekAttribution },
  [ReportView.Daypart]: { action: getDaypartAttributionReport, reportRequest: ReportRequest.DaypartAttribution },
  [ReportView.Deals]: { action: getDealsAttributionReport, reportRequest: ReportRequest.DealsAttribution },
  // Delivery-only + hidden in Attribution; present only to satisfy the exhaustive record.
  [ReportView.ContentNetwork]: {
    action: getContentNetworkDeliveryReport,
    reportRequest: ReportRequest.ContentNetworkDelivery,
  },
  [ReportView.ContentChannel]: {
    action: getContentChannelDeliveryReport,
    reportRequest: ReportRequest.ContentChannelDelivery,
  },
  [MapScale.Dma]: { action: getDmaAttributionReport, reportRequest: ReportRequest.DmaAttribution },
  [MapScale.County]: { action: getCountyAttributionReport, reportRequest: ReportRequest.CountyAttribution },
  [MapScale.Zip]: { action: getZipAttributionReport, reportRequest: ReportRequest.ZipAttribution },
  [MapScale.CongressionalDistricts]: {
    action: getCongressionalAttributionReport,
    reportRequest: ReportRequest.CongressionalAttribution,
  },
  [MapScale.City]: { action: getCityAttributionReport, reportRequest: ReportRequest.CityAttribution },
  [MapScale.State]: { action: getStateAttributionReport, reportRequest: ReportRequest.StateAttribution },
};

export const viewTypeDispatch = {
  [ViewType.Attribution]: attributionReportDispatch,
  [ViewType.Delivery]: deliveryReportDispatch,
};
//#endregion

//#region boundaries
export const getZipBoundaries = createAsyncThunk<IGeoJson, { zipCodes: string[] } & IAbortSignalThunkArg>(
  ActionPrefix.ZipBoundaries,
  async payload => {
    const { zipCodes, signal } = payload || {};
    const responseResult = await axios.get(ZIPCODE_ENDPOINT, {
      params: { zipcode: zipCodes.join(',') },
      headers: borderIOHeaders,
      signal,
    });

    return responseResult.data;
  },
);

export const getDmaBoundaries = createAsyncThunk<IGeoJson, IAbortSignalThunkArg>(
  ActionPrefix.DmaBoundaries,
  async (payload, { getState }) => {
    const {
      reportingDashboardBeeswax: { dmaDeliveryReport },
    } = getState() as RootState;
    const { signal } = payload || {};
    const requestBody = getBoundariesRequestBody(MAP_SCALES.DMA, dmaDeliveryReport, BeeswaxReportProperties.DMA_CODE);
    if (!isPostableBoundariesBody(requestBody)) return EMPTY_FEATURE_COLLECTION;
    const responseResult = await axios.post(GEO_BOUNDARIES, requestBody, {
      signal,
    });

    return responseResult.data;
  },
);

export const getStateBoundaries = createAsyncThunk<IGeoJson, IAbortSignalThunkArg>(
  ActionPrefix.StateBoundaries,
  async (payload, { getState }) => {
    const {
      reportingDashboardBeeswax: { stateDeliveryReport },
    } = getState() as RootState;
    const { signal } = payload || {};
    const requestBody = getBoundariesRequestBody(
      MAP_SCALES.STATE,
      stateDeliveryReport,
      BeeswaxReportProperties.GEO_STATE,
    );
    if (!isPostableBoundariesBody(requestBody)) return EMPTY_FEATURE_COLLECTION;
    const responseResult = await axios.post(GEO_BOUNDARIES, requestBody, {
      signal,
    });

    return responseResult.data;
  },
);

export const getCountyBoundaries = createAsyncThunk<IGeoJson, IAbortSignalThunkArg>(
  ActionPrefix.CountyBoundaries,
  async (payload, { getState }) => {
    const {
      reportingDashboardBeeswax: { countyDeliveryReport },
    } = getState() as RootState;
    const { signal } = payload || {};
    const requestBody = getBoundariesRequestBody(
      MAP_SCALES.COUNTY,
      countyDeliveryReport,
      BeeswaxReportProperties.GEO_COUNTY_ID,
      250,
    );
    if (!isPostableBoundariesBody(requestBody)) return EMPTY_FEATURE_COLLECTION;
    const responseResult = await axios.post(GEO_BOUNDARIES, requestBody, {
      signal,
    });

    return responseResult.data;
  },
);

export const getCongressionalDistrictBoundaries = createAsyncThunk<IGeoJson, IAbortSignalThunkArg>(
  ActionPrefix.CongressionalDistrictBoundaries,
  async (payload, { getState }) => {
    const {
      reportingDashboardBeeswax: { congressionalDistrictDeliveryReport },
    } = getState() as RootState;
    const { signal } = payload || {};
    const requestBody = getCongressionalBoundariesRequestBody(
      MAP_SCALES.CONGRESSIONAL_DISTRICTS,
      congressionalDistrictDeliveryReport,
      200,
    );
    if (!isPostableBoundariesBody(requestBody)) return EMPTY_FEATURE_COLLECTION;
    const responseResult = await axios.post(GEO_BOUNDARIES, requestBody, {
      signal,
    });

    return responseResult.data;
  },
);

export const getCityBoundaries = createAsyncThunk<IGeoJson, IAbortSignalThunkArg>(
  ActionPrefix.CityBoundaries,
  async (payload, { getState }) => {
    const {
      reportingDashboardBeeswax: { cityDeliveryReport },
    } = getState() as RootState;
    const { signal } = payload || {};
    const cityCodes = cityDeliveryReport
      .map(item => item[BeeswaxReportProperties.CITY_CODE])
      .filter(item => item !== '-1')
      .slice(0, 100);

    const response = await axios.post(
      GEO_JOINT_BOUNDARIES,
      { data: [{ geoType: MAP_SCALES.CITY, geoIds: cityCodes }] },
      { signal },
    );

    return response.data?.find(({ type }: { type: string }) => type === MAP_SCALES.CITY)?.featureCollection || [];
  },
);
//#endregion
//#region reducers
export const reportMap: RequestMap<IState> = [
  ['channelDeliveryReport', getChannelDeliveryReport],
  ['deviceDeliveryReport', getDeviceDeliveryReport],
  ['advertiserDeliveryReport', getAdvertiserDeliveryReport],
  ['creativeDeliveryReport', getCreativeDeliveryReport],
  ['publisherDeliveryReport', getPublisherDeliveryReport],
  ['dayOfWeekDeliveryReport', getDayOfWeekDeliveryReport],
  ['daypartDeliveryReport', getDaypartDeliveryReport],
  ['dealsDeliveryReport', getDealsDeliveryReport],
  ['contentNetworkDeliveryReport', getContentNetworkDeliveryReport],
  ['contentChannelDeliveryReport', getContentChannelDeliveryReport],
  ['stateDeliveryReport', getStateDeliveryReport],
  ['dmaDeliveryReport', getDmaDeliveryReport],
  ['countyDeliveryReport', getCountyDeliveryReport],
  ['zipDeliveryReport', getZipDeliveryReport],
  ['congressionalDistrictDeliveryReport', getCongressionalDeliveryReport],
  ['cityDeliveryReport', getCityDeliveryReport],
  ['dateDeliveryReport', getDateDeliveryReport],
  ['weekDeliveryReport', getWeekDeliveryReport],
  ['monthDeliveryReport', getMonthDeliveryReport],
  ['quarterDeliveryReport', getQuarterDeliveryReport],

  ['dateAttributionReport', getDateAttributionReport],
  ['weekAttributionReport', getWeekAttributionReport],
  ['monthAttributionReport', getMonthAttributionReport],
  ['quarterAttributionReport', getQuarterAttributionReport],
  ['channelAttributionReport', getChannelAttributionReport],
  ['deviceAttributionReport', getDeviceAttributionReport],
  ['advertiserAttributionReport', getAdvertiserAttributionReport],
  ['campaignAttributionReport', getCampaignAttributionReport],
  ['placementAttributionReport', getPlacementAttributionReport],
  ['creativeAttributionReport', getCreativeAttributionReport],
  ['publisherAttributionReport', getPublisherAttributionReport],
  ['dayOfWeekAttributionReport', getDayOfWeekAttributionReport],
  ['daypartAttributionReport', getDaypartAttributionReport],
  ['dealsAttributionReport', getDealsAttributionReport],
  ['stateAttributionReport', getStateAttributionReport],
  ['dmaAttributionReport', getDmaAttributionReport],
  ['countyAttributionReport', getCountyAttributionReport],
  ['zipAttributionReport', getZipAttributionReport],
  ['congressionalDistrictAttributionReport', getCongressionalAttributionReport],
  ['cityAttributionReport', getCityAttributionReport],
];

export const boundaryMap: RequestMap<IState> = [
  ['zipBoundariesData', getZipBoundaries],
  ['stateBoundariesData', getStateBoundaries],
  ['countyBoundariesData', getCountyBoundaries],
  ['congressionalDistrictBoundariesData', getCongressionalDistrictBoundaries],
  ['dmaBoundariesData', getDmaBoundaries],
  ['cityBoundariesData', getCityBoundaries],
];
//#endregion
